import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSiteLocateState from '../hooks/useSiteLocateState';
import { polygonCentroid } from '../services/osmBuildings';
import {
  DEFAULT_SITE_PLANNER_MODEL,
  catalogEntry,
  catalogLabel,
} from '../data/sitePlannerCatalog';
import {
  preloadCatalogModel,
  preloadCatalogQueue,
} from '../services/catalogModelPreload';

function newId() {
  return `s_${Math.random().toString(36).slice(2, 9)}`;
}

const XlabsConfiguratorContext = createContext(null);

/**
 * Shared Locate + Design/Place session for /xlabs/locate and /xlabs/design.
 * See docs/XLABS_DESIGN.md · docs/XLABS_LOCATE.md
 */
export function XlabsConfiguratorProvider({ children }) {
  const locate = useSiteLocateState();
  const [structures, setStructures] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [catalogType, setCatalogType] = useState(DEFAULT_SITE_PLANNER_MODEL);
  const [preloadSlug, setPreloadSlug] = useState(null);
  const [loadingStructures, setLoadingStructures] = useState({});

  const { loadSite: loadSiteBase, lot, setLotSelected } = locate;

  const loadSite = useCallback(
    async (addressOverride) => {
      setStructures([]);
      setSelectedId(null);
      setLoadingStructures({});
      await loadSiteBase(addressOverride);
    },
    [loadSiteBase],
  );

  useEffect(() => {
    if (!catalogType) return undefined;
    let cancelled = false;
    setPreloadSlug(catalogType);
    preloadCatalogModel(catalogType)
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setPreloadSlug((cur) => (cur === catalogType ? null : cur));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [catalogType]);

  useEffect(() => {
    const ac = new AbortController();
    preloadCatalogQueue({ concurrency: 2, signal: ac.signal }).catch(() => {});
    return () => ac.abort();
  }, []);

  const onStructureLoadChange = useCallback((id, loading, type) => {
    setLoadingStructures((prev) => {
      if (loading) {
        if (prev[id] === type) return prev;
        return { ...prev, [id]: type || true };
      }
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const modelLoadLabel = useMemo(() => {
    const ids = Object.keys(loadingStructures);
    if (ids.length) {
      const type = loadingStructures[ids[ids.length - 1]];
      return typeof type === 'string' ? catalogLabel(type) : 'model';
    }
    if (preloadSlug) return catalogLabel(preloadSlug);
    return null;
  }, [loadingStructures, preloadSlug]);

  const addStructure = useCallback(
    (type = catalogType) => {
      const slug = catalogEntry(type)?.slug || DEFAULT_SITE_PLANNER_MODEL;
      preloadCatalogModel(slug).catch(() => {});
      const id = newId();
      const verts = lot?.vertices || [];
      const [bx, bz] = verts.length ? polygonCentroid(verts) : [0, 0];
      setStructures((list) => [
        ...list,
        {
          id,
          type: slug,
          position: [bx, 0, bz],
          rotationY: 0,
        },
      ]);
      setSelectedId(id);
      setLotSelected(false);
    },
    [catalogType, lot?.vertices, setLotSelected],
  );

  const removeStructure = useCallback((id) => {
    setStructures((list) => list.filter((s) => s.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
    setLoadingStructures((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updateStructure = useCallback((id, patch) => {
    setStructures((list) =>
      list.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    );
  }, []);

  const siteContentReady =
    locate.contextMode !== 'google' ||
    (locate.floorY != null && Number.isFinite(locate.floorY));

  const value = {
    ...locate,
    loadSite,
    structures,
    selectedId,
    setSelectedId,
    catalogType,
    setCatalogType,
    addStructure,
    removeStructure,
    updateStructure,
    onStructureLoadChange,
    modelLoadLabel,
    preloadSlug,
    siteContentReady,
  };

  return (
    <XlabsConfiguratorContext.Provider value={value}>
      {children}
    </XlabsConfiguratorContext.Provider>
  );
}

export function useXlabsConfigurator() {
  const ctx = useContext(XlabsConfiguratorContext);
  if (!ctx) {
    throw new Error(
      'useXlabsConfigurator must be used within XlabsConfiguratorProvider',
    );
  }
  return ctx;
}
